import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { ContributionDetailDialog } from '@/components/giving/contribution-detail-dialog';
import { FundSummaryDialog } from '@/components/giving/fund-summary-dialog';
import {
  AuditTrailPanel,
  type AuditRelated,
} from '@/components/shared/audit-trail-panel';
import {
  DateRangeFilter,
  resolvePreset,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { FilterBar } from '@/components/shared/filter-bar';
import { PageHeader } from '@/components/shared/page-header';
import { Pagination } from '@/components/shared/pagination';
import { SearchInput } from '@/components/shared/search-input';
import { Button } from '@/components/ui/button';
import { Listbox } from '@/components/ui/listbox';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { hasPermission, useCurrentMembership } from '@/lib/permissions';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { downloadFinancialAuditPdf } from '@/utils/financial-audit-pdf';
import { formatDateTime } from '@/utils/format';

import {
  type AuditLogRow,
  FINANCIAL_AUDIT_ACTIONS,
  FINANCIAL_AUDIT_ENTITY_TYPES,
  presentAuditEntry,
} from './audit-format';
import { downloadAuditCsv } from './download-audit-csv';
import { fetchFinancialAuditForPdf } from './fetch-financial-audit-for-pdf';

export const FinancialAuditPage = () => {
  const { t } = useTranslation('audit');
  const { t: tCommon } = useTranslation();
  const { t: tGiving } = useTranslation('giving');
  const navigate = useNavigate();
  const timezone = usePreferences((state) => state.timezone);
  const locale = usePreferences((state) => state.locale);
  const membership = useCurrentMembership();
  const church = useChurchStore((state) => state.church);
  const churchId = church?.id ?? null;
  const canExport = hasPermission(membership, 'AUDIT_EXPORT');

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [contributionId, setContributionId] = useState<string | null>(null);
  const [fundId, setFundId] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    preset: 'allTime',
    ...resolvePreset('allTime', timezone),
  }));

  const openRelated = (related: AuditRelated) => {
    if (related.kind === 'contribution' && related.id) {
      setContributionId(related.id);
      return;
    }
    if (related.kind === 'fund' && related.id) {
      setFundId(related.id);
      return;
    }
    if (related.href) {
      void navigate(related.href);
    }
  };

  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;
  const appliedCount = [
    search.trim(),
    action,
    entityType,
    dateRange.preset !== 'allTime' ? dateRange.preset : null,
  ].filter(Boolean).length;

  const actionOptions = useMemo(
    () => [
      { value: '', label: t('filters.allActions') },
      ...FINANCIAL_AUDIT_ACTIONS.map((value) => ({
        value,
        label: t(`actions.${value}`),
      })),
    ],
    [t],
  );

  const entityTypeOptions = useMemo(
    () => [
      { value: '', label: t('filters.allTypes') },
      ...FINANCIAL_AUDIT_ENTITY_TYPES.map((value) => ({
        value,
        label: t(`entityTypes.${value}`),
      })),
    ],
    [t],
  );

  const query = useQuery({
    queryKey: [
      'audit-logs-financial',
      page,
      pageSize,
      search,
      action,
      entityType,
      dateFrom,
      dateTo,
    ],
    queryFn: () =>
      api.get<{ data: AuditLogRow[]; page: { totalItems: number; totalPages: number } }>(
        '/audit/logs',
        {
          query: {
            page,
            pageSize,
            scope: 'financial',
            ...(search.trim() ? { q: search.trim() } : {}),
            ...(action ? { action } : {}),
            ...(entityType ? { entityType } : {}),
            ...(dateFrom ? { dateFrom } : {}),
            ...(dateTo ? { dateTo } : {}),
            sort: '-createdAt',
          },
        },
      ),
  });

  const exportMutation = useMutation({
    mutationFn: async () => {
      if (!churchId) throw new Error('No church');
      await downloadAuditCsv({
        churchId,
        fallbackFilename: 'financial-activity.csv',
        query: {
          scope: 'financial',
          ...(search.trim() ? { q: search.trim() } : {}),
          ...(action ? { action } : {}),
          ...(entityType ? { entityType } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
      });
    },
    onError: () => {
      toast.error(t('financial.exportFailed'));
    },
  });

  const pdfMutation = useMutation({
    mutationFn: async () => {
      const rows = await fetchFinancialAuditForPdf({
        search: search.trim() || undefined,
        action: action || undefined,
        entityType: entityType || undefined,
        dateFrom,
        dateTo,
      });
      const presented = rows.map((row) => {
        const entry = presentAuditEntry(row, {
          t,
          tGiving,
          locale,
          defaultCurrency: church?.baseCurrency ?? null,
        });
        return {
          ...entry,
          at: formatDateTime(row.createdAt, { locale, timezone, withZone: true }),
        };
      });

      const filterParts: string[] = [];
      if (search.trim()) filterParts.push(search.trim());
      if (action) filterParts.push(t(`actions.${action}`, { defaultValue: action }));
      if (entityType) {
        filterParts.push(t(`entityTypes.${entityType}`, { defaultValue: entityType }));
      }
      if (dateFrom || dateTo) {
        filterParts.push(
          [dateFrom, dateTo].filter(Boolean).join(' – ') || tCommon('date.allTime'),
        );
      }

      await downloadFinancialAuditPdf({
        churchName: church?.name ?? t('financial.title'),
        branchName: church?.campusName,
        logoUrl: church?.logoUrl,
        title: t('financial.title'),
        description: t('financial.description'),
        generatedLabel: t('financial.pdfGenerated', {
          when: formatDateTime(new Date().toISOString(), {
            locale,
            timezone,
            withZone: true,
          }),
        }),
        metaLines: [
          filterParts.length > 0
            ? t('financial.pdfFilters', { filters: filterParts.join(' · ') })
            : t('financial.pdfNoFilters'),
        ],
        columns: {
          when: t('financial.pdfColumns.when'),
          summary: t('financial.pdfColumns.summary'),
          actor: t('financial.pdfColumns.actor'),
          details: t('financial.pdfColumns.details'),
        },
        emptyValue: t('emptyValue'),
        filename: 'financial-activity.pdf',
        entries: presented,
      });
    },
    onError: () => {
      toast.error(t('financial.pdfFailed'));
    },
  });

  const entries = useMemo(
    () =>
      (query.data?.data ?? []).map((row) =>
        presentAuditEntry(row, {
          t,
          tGiving,
          locale,
          defaultCurrency: church?.baseCurrency ?? null,
        }),
      ),
    [locale, query.data, t, tGiving],
  );

  const pageMeta = query.data?.page;
  const exportBusy = exportMutation.isPending || pdfMutation.isPending;

  return (
    <div>
      <PageHeader
        title={t('financial.title')}
        description={t('financial.description')}
        tipId="settings.audit.financial"
        headerActions={
          canExport ? (
            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={() => pdfMutation.mutate()}
                disabled={exportBusy || !churchId}
              >
                {pdfMutation.isPending
                  ? t('financial.generatingPdf')
                  : t('financial.downloadPdf')}
              </Button>
              <Button
                onClick={() => exportMutation.mutate()}
                disabled={exportBusy || !churchId}
              >
                {t('financial.export')}
              </Button>
            </div>
          ) : null
        }
      />

      <div>
        <FilterBar
          appliedCount={appliedCount}
          onClearAll={() => {
            setSearch('');
            setAction('');
            setEntityType('');
            setDateRange({
              preset: 'allTime',
              ...resolvePreset('allTime', timezone),
            });
            setPage(1);
          }}
          labels={{
            filters: t('filters.title'),
            clearAll: t('filters.clearAll'),
            applied: (count) => t('filters.applied', { count }),
          }}
          leading={
            <SearchInput
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              placeholder={t('filters.search')}
              clearLabel={tCommon('search.clear')}
              className="max-w-none sm:w-56"
              aria-label={t('filters.search')}
            />
          }
        >
          {({ popoverPortal }) => (
            <>
              <div className="w-56 min-w-[12rem]">
                <Listbox
                  value={action}
                  onChange={(value) => {
                    setAction(value ?? '');
                    setPage(1);
                  }}
                  options={actionOptions}
                  placeholder={t('filters.allActions')}
                  searchable
                  searchPlaceholder={t('filters.searchActions')}
                  emptyMessage={t('filters.noMatchingActions')}
                  aria-label={t('filters.action')}
                  popoverPortal={popoverPortal}
                />
              </div>
              <div className="w-48 min-w-[10rem]">
                <Listbox
                  value={entityType}
                  onChange={(value) => {
                    setEntityType(value ?? '');
                    setPage(1);
                  }}
                  options={entityTypeOptions}
                  placeholder={t('filters.allTypes')}
                  aria-label={t('filters.entityType')}
                  popoverPortal={popoverPortal}
                />
              </div>
              <DateRangeFilter
                value={dateRange}
                onChange={(range) => {
                  setDateRange(range);
                  setPage(1);
                }}
                popoverPortal={popoverPortal}
                labels={{
                  today: tCommon('date.today'),
                  yesterday: tCommon('date.yesterday'),
                  thisWeek: tCommon('date.thisWeek'),
                  lastWeek: tCommon('date.lastWeek'),
                  thisMonth: tCommon('date.thisMonth'),
                  lastMonth: tCommon('date.lastMonth'),
                  thisQuarter: tCommon('date.thisQuarter'),
                  thisYear: tCommon('date.thisYear'),
                  lastYear: tCommon('date.lastYear'),
                  allTime: tCommon('date.allTime'),
                  custom: tCommon('date.custom'),
                  from: t('filters.dateFrom'),
                  to: t('filters.dateTo'),
                }}
              />
            </>
          )}
        </FilterBar>
      </div>

      <AuditTrailPanel
        title={t('financial.title')}
        entries={entries}
        loading={query.isLoading}
        emptyTitle={
          search.trim() ? t('financialEmptyFiltered.title') : t('financialEmpty.title')
        }
        emptyBody={
          search.trim() ? t('financialEmptyFiltered.body') : t('financialEmpty.body')
        }
        emptyValueLabel={t('emptyValue')}
        onOpenRelated={openRelated}
      />

      <ContributionDetailDialog
        contributionId={contributionId}
        open={Boolean(contributionId)}
        onOpenChange={(open) => {
          if (!open) setContributionId(null);
        }}
      />
      <FundSummaryDialog
        fundId={fundId}
        open={Boolean(fundId)}
        onOpenChange={(open) => {
          if (!open) setFundId(null);
        }}
      />

      {pageMeta ? (
        <Pagination
          className="mt-4"
          page={page}
          pageSize={pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={{
            showing: ({ from, to, total }) =>
              tCommon('pagination.showing', { from, to, total }),
            perPage: tCommon('pagination.perPage'),
            first: tCommon('pagination.first'),
            previous: tCommon('pagination.previous'),
            next: tCommon('pagination.next'),
            last: tCommon('pagination.last'),
          }}
          disabled={query.isFetching}
        />
      ) : null}
    </div>
  );
};
